import { ClickAwayListener } from '@mui/base';
import { Box, Divider, Input } from '@mui/joy';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { debounce } from 'lodash';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthUser } from 'auth/hooks';
import { useGetLegacyVendorsSearch, useGetVendorsSearch } from 'contacts/hooks';
import { IContactGroupAddress, IVendorSearchRequestPayload } from 'contacts/types';
import { AlternateVersionHelper } from 'core/helpers';
import { trimFilterText } from 'core/utils';
import { Direction, Orientation, Variant } from 'theme/enums';

import {
  DEBOUNCE_TIME,
  DETAILS_WIDTH,
  LIST_HEIGHT,
  LIST_WIDTH,
  MIN_CHARS_TO_SEARCH,
  PAGE_SIZE,
} from './constants';
import { AlternateVendorDetails, VendorList } from './VendorList';

export const VendorSearch = () => {
  const [inputValue, setInputValue] = useState('');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState<IContactGroupAddress | null>(null);

  const { t } = useTranslation();
  const { user } = useAuthUser();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ user });

  const requestPayload: IVendorSearchRequestPayload = useMemo(
    () => ({
      pageSize: PAGE_SIZE,
      filters: { query },
    }),
    [query]
  );

  const alternateResult = useGetVendorsSearch(requestPayload, {
    enabled: isOpen && query?.length >= MIN_CHARS_TO_SEARCH && isAlternateVersionEnabled,
  });

  const legacyResult = useGetLegacyVendorsSearch(requestPayload, {
    enabled: isOpen && query?.length >= MIN_CHARS_TO_SEARCH && !isAlternateVersionEnabled,
  });

  const { records, count, hasNextPage, fetchNextPage, isLoading } = isAlternateVersionEnabled
    ? alternateResult
    : legacyResult;

  const debouncedSetQuery = useRef(
    debounce((value: string) => {
      setQuery(trimFilterText(value));
      setSelectedVendor(null);
    }, DEBOUNCE_TIME)
  ).current;

  const onInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setIsOpen(true);
      debouncedSetQuery(e.target.value);
    },
    [debouncedSetQuery]
  );

  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const onFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback(() => {
    debouncedSetQuery.cancel();
    setIsOpen(false);
    setQuery('');
    setInputValue('');
    setSelectedVendor(null);
  }, [debouncedSetQuery]);

  const onVendorInfo = useCallback(
    (vendor: IContactGroupAddress) => {
      if (selectedVendor?.id === vendor.id) {
        setSelectedVendor(null);

        return;
      }

      setSelectedVendor(vendor);
    },
    [selectedVendor?.id]
  );

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Box sx={{ position: 'relative', flex: 1 }}>
        <Input
          value={inputValue}
          onChange={onInputChange}
          onFocus={onFocus}
          placeholder={t('placeholderVendorSearch')}
          variant={Variant.PLAIN}
          endDecorator={<MagnifyingGlass />}
          sx={{ flex: 1, border: 'none', '--Input-focusedThickness': '0px' }}
        />

        {isOpen && (
          <Box
            sx={{
              position: 'absolute',
              top: 'calc(100% + 8px)',
              left: 0,
              display: 'flex',
              flexDirection: Direction.ROW,
              backgroundColor: 'background.surface',
              borderRadius: '6px',
              border: '1px solid',
              borderColor: 'neutral.300',
              boxShadow: 'md',
              maxHeight: LIST_HEIGHT,
              zIndex: 10,
            }}
          >
            <Box sx={{ width: LIST_WIDTH, flexShrink: 0, padding: 1.5 }}>
              <VendorList
                records={records}
                count={count}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
                isLoading={isLoading}
                minCharsToSearch={MIN_CHARS_TO_SEARCH}
                query={query}
                selectedVendorId={selectedVendor?.id}
                onVendorInfo={onVendorInfo}
                isAlternateVersionEnabled={isAlternateVersionEnabled}
              />
            </Box>

            {isAlternateVersionEnabled && selectedVendor && (
              <>
                <Divider orientation={Orientation.VERTICAL} />
                <Box
                  sx={{
                    width: DETAILS_WIDTH,
                    flexShrink: 0,
                    overflowY: 'auto',
                    padding: 1.5,
                  }}
                >
                  <AlternateVendorDetails vendor={selectedVendor} />
                </Box>
              </>
            )}
          </Box>
        )}
      </Box>
    </ClickAwayListener>
  );
};
